import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

const About = () => {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>About — Family Policy Loan Planner</title>
        <meta name="description" content="Learn about the Family Policy Loan Planner, inspired by R. Nelson Nash's Infinite Banking Concept and developed by Richard L. Canfield." />
        <link rel="canonical" href="https://app.familyloanplanner.com/about" />
        <meta property="og:title" content="About — Family Policy Loan Planner" />
        <meta property="og:description" content="Learn about the Family Policy Loan Planner, inspired by the Infinite Banking Concept." />
        <meta property="og:url" content="https://app.familyloanplanner.com/about" />
      </Helmet>
      <header className="bg-primary border-b border-border">
        <div className="container mx-auto flex items-center h-16 px-4 lg:px-8 gap-4">
          <Link to="/">
            <Button variant="ghost" size="sm" className="text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back to Planner
            </Button>
          </Link>
          <h1 className="text-base sm:text-lg font-semibold text-primary-foreground tracking-tight">
            About — Family Policy Loan Planner™
          </h1>
        </div>
      </header>

      <main className="container mx-auto px-4 lg:px-8 py-8 sm:py-12 max-w-3xl space-y-8">
        {/* What It Is */}
        <section className="space-y-4">
          <h2 className="text-2xl font-bold tracking-tight">What Is This Tool?</h2>
          <p className="text-muted-foreground leading-relaxed">
            The <strong className="text-foreground">Family Policy Loan Planner™</strong> is a planning tool designed to help policy owners and advisors estimate policy loan interest, repayment behavior, and financing recapture opportunities.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            It supports more intentional, disciplined repayment planning using both rate-based and payment-based solving — giving families and advisors a practical way to plan how they repay policy loans and recapture the financing margin within their own family system.
          </p>
        </section>

        {/* Infinite Banking Concept */}
        <Card className="border-accent/30 bg-accent/5">
          <CardContent className="pt-6 space-y-4">
            <h3 className="text-lg font-semibold">Inspired by the Infinite Banking Concept</h3>
            <p className="text-muted-foreground leading-relaxed">
              This tool is inspired by the <strong className="text-foreground">Infinite Banking Concept</strong>, created by <strong className="text-foreground">R. Nelson Nash</strong>. Nelson Nash's work laid the foundation for understanding how participating whole life insurance can serve as a personal banking system — where the policy owner becomes both the borrower and the banker.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Nash's vision was simple and profound: take control of your financial environment by financing through your own system rather than giving control to outside institutions. His book, <em>Becoming Your Own Banker</em>, remains a cornerstone of the movement.
            </p>
            <p className="text-sm text-muted-foreground italic">
              We honor R. Nelson Nash's contribution to financial education and the lasting impact of his work.
            </p>
          </CardContent>
        </Card>

        {/* Origin Story */}
        <section className="space-y-4">
          <h2 className="text-2xl font-bold tracking-tight">Origin</h2>
          <p className="text-muted-foreground leading-relaxed">
            Developed from a concept originated by <strong className="text-foreground">Richard L. Canfield</strong> of Alberta, Canada.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            Richard Canfield originally conceived the need for this type of tool in 2019 — recognizing that advisors and policy owners needed a practical, behavior-focused planning calculator that could model intentional loan repayment, not just illustrate policy values. At the time, the technology to build it fully did not yet exist. By 2026, the vision became reality.
          </p>
        </section>

        {/* Richard Canfield Bio */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            <h3 className="text-lg font-semibold">About Richard L. Canfield</h3>
            <p className="text-muted-foreground leading-relaxed">
              Richard Canfield is an <strong className="text-foreground">Authorized Infinite Banking Practitioner</strong>, educator, advisor, and creator dedicated to helping families and business owners build control, stewardship, and legacy through Family Banking.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              His work focuses on bridging the gap between financial concept and real-world behavior — ensuring that the principles of the Infinite Banking Concept translate into practical, disciplined action for the families and businesses he serves.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Through education, tools, and direct advisory, Richard champions the belief that financial control begins at home — with intentional systems, committed repayment, and the discipline to keep financial energy within the family.
            </p>
          </CardContent>
        </Card>

        <div className="pt-4 flex gap-3">
          <Link to="/">
            <Button>Back to Planner</Button>
          </Link>
          <Link to="/faq">
            <Button variant="outline">Read FAQ</Button>
          </Link>
        </div>
      </main>
    </div>
  );
};

export default About;
